import { useInfiniteQuery, useQuery } from 'react-query';
import {
  getActivities,
  getAgreementMagazines,
  getBenefits,
  getCheckPermission,
  getContract,
  getEconometer,
  getFaturas,
  getNotifications,
  getPromotionDetails,
  getPromotions,
  getSales,
} from './api';
import { RequestParameters } from '../types';

export enum ClubHook {
  Activities = 'activities',
  Sales = 'sales',
  Promotions = 'promotions',
  Benefits = 'benefits',
  PromotionDetails = 'promotionDetail',
  Econometer = 'econometer',
  CheckPermission = 'checkPermission',
  Notification = 'notifications',
  Contract = 'contract',
  Faturas = 'faturas',
  RevistasDigitais = 'revistasDigitais',
}

export function useActivities() {
  return useQuery([ClubHook.Activities], () => getActivities());
}

export function useGetContract() {
  return useQuery([ClubHook.Contract], () => getContract(), {
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}



export function useGetFaturas() {
  return useQuery([ClubHook.Faturas], () => getFaturas(), {
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

export function usePromotions(id?: number | null, params?: RequestParameters) {
  return useQuery([ClubHook.Promotions, params], () => getPromotions({ ...params, agreement: id }));
}

export function usePromotionsInfinity(name?: string, id?: number | null, params?: RequestParameters) {
  return useInfiniteQuery({
    queryKey: [name ? `promotions-${name}` : ClubHook.Promotions, name, params],
    queryFn: ({ pageParam = 1 }) => getPromotions({ ...params, agreement_id: id, page: pageParam }),
    getNextPageParam: (lastPage, allPages) => (lastPage.length > 0 ? allPages.length + 1 : undefined),
    refetchOnWindowFocus: false,
    enabled: !!id,
    retry: false,
  });
}

export function useNotificationsInfinity(id?: number | null, params?: RequestParameters) {
  return useInfiniteQuery({
    queryKey: [ClubHook.Notification, params],
    queryFn: ({ pageParam = 1 }) => getNotifications({ agreement: id, page: pageParam, ...params }),
    getNextPageParam: (lastPage, allPages) => (lastPage.length > 0 ? allPages.length + 1 : undefined),
    refetchOnWindowFocus: false,
    enabled: !!id,
  });
}

export function usePromotionDetails(id: string) {
  return useQuery([`${ClubHook.PromotionDetails}-${id}`, id], () => getPromotionDetails(id));
}

export function useHistory() {
  return useQuery([ClubHook.Econometer], () => getEconometer());
}

export function useSales(agreementId?: number) {
  return useQuery([ClubHook.Sales], () =>
    getSales({
      agreement: agreementId,
      page_size: 100,
    })
  );
}

export function useRevistasDigitaisInfinity(agreementId: number, params?: RequestParameters) {
  return useInfiniteQuery({
    queryKey: [ClubHook.RevistasDigitais, params],
    queryFn: ({ pageParam = 1 }) => getAgreementMagazines(agreementId, {
      page_size: params?.limit || 12,
      page: pageParam,
      ...params,
    }),
    getNextPageParam: (lastPage, allPages) => (lastPage.length > 0 ? allPages.length + 1 : undefined),
    refetchOnWindowFocus: false,
    enabled: !!agreementId,
    retry: false,
  });
}

export function useBenefits(params?: RequestParameters) {
  return useQuery([ClubHook.Benefits, params], () => getBenefits({ ...params, page_size: 100 }));
}

export function useCheckPermission(agreementId: number) {
  return useQuery([ClubHook.CheckPermission, agreementId], () => getCheckPermission({ agreement: agreementId }), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    enabled: !!agreementId,
  });
}
